import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Settings, Globe, Sun, Moon } from 'lucide-react';
import CurrencySettings from '../Layout/CurrencySettings.jsx';
import { useTheme } from '../../contexts/ThemeContext';

const AffiliateSettingsModal = ({ isOpen, onClose, onLanguageChange }) => {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme, isDark } = useTheme();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    onLanguageChange(newLang);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className={`bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-border">
          <div className={`flex items-center justify-between ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3 w-full' : 'space-x-3'}`}>
              <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Settings className="h-5 w-5 text-primary" />
              </div>
              <div className={i18n.language === 'ar' ? 'text-right w-full' : 'text-left'}>
                <h2 className="text-xl font-semibold text-card-foreground">
                  {t('settings.title')}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t('settings.subtitle')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-muted-foreground hover:text-card-foreground hover:bg-muted rounded-md transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Theme Settings */}
          <div className="mb-6">
            <div className={`flex items-center mb-4 ${i18n.language === 'ar' ? 'flex-row-reverse space-x-reverse space-x-2 justify-start w-full' : 'space-x-2'}`}>
              {isDark ? (
                <Moon className="h-5 w-5 text-muted-foreground" />
              ) : (
                <Sun className="h-5 w-5 text-muted-foreground" />
              )}
              <h3 className={`text-lg font-medium text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('settings.themeSettings')}</h3>
            </div>
            
            <div className="space-y-3">
              <div className={`flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center ${i18n.language === 'ar' ? 'space-x-reverse space-x-3 text-right' : 'space-x-3 text-left'}`}>
                  <div className={`h-4 w-4 rounded-full ${isDark ? 'bg-primary' : 'bg-muted-foreground'}`}></div>
                  <div className={i18n.language === 'ar' ? 'text-right' : 'text-left'}>
                    <p className="text-sm font-medium text-card-foreground">
                      {isDark ? t('settings.darkMode') : t('settings.lightMode')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {isDark ? t('settings.darkThemeActive') : t('settings.lightThemeActive')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={toggleTheme}
                  className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
                >
                  {isDark ? t('settings.switchToLight') : t('settings.switchToDark')}
                </button>
              </div>
            </div>
          </div>

          {/* Currency Settings */}
          <div className="mb-6 pt-6 border-t border-border">
            <CurrencySettings />
          </div>

          {/* Language Settings */}
          <div className="mb-6 pt-6 border-t border-border">
            <div className={`flex items-center mb-4 ${i18n.language === 'ar' ? 'flex-row-reverse space-x-reverse space-x-2 justify-start w-full' : 'space-x-2'}`}>
              <Globe className="h-5 w-5 text-muted-foreground" />
              <h3 className={`text-lg font-medium text-card-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>{t('settings.languageSettings')}</h3>
            </div>
            
            <div className={`space-y-3 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}>
              <button
                onClick={(e) => {
                  toggleLanguage();
                  e.target.blur();
                }}
                className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
              >
                {i18n.language === 'en' ? t('settings.switchToArabic') : t('settings.switchToEnglish')}
              </button>
              
              <div className="text-sm text-muted-foreground">
                {t('settings.currentLanguage')}: {i18n.language === 'en' ? 'English' : 'العربية'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateSettingsModal;
